import type { Booking } from '@/components/BookingCalendar';
import { parseISO, isSameDay, addHours, format, isBefore, isAfter, addDays, isValid } from 'date-fns';

export type BookingConflict = {
  type: 'overlap' | 'adjacent' | 'same_client';
  severity: 'error' | 'warning';
  message: string;
  booking: Booking;
  suggestedAlternatives: {
    date: string;
    time: string;
    room?: number;
  }[];
};

// Helper function to safely parse date strings
export const safeParseISO = (dateStr: string, timeStr: string): Date | null => {
  try {
    // Check if the date string already contains a time component (T)
    const cleanDateStr = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const dateTimeStr = `${cleanDateStr}T${timeStr}`;
    
    const parsedDate = parseISO(dateTimeStr);
    if (!isValid(parsedDate)) {
      console.warn(`Invalid date after parsing: ${dateTimeStr}`);
      return null;
    }
    return parsedDate;
  } catch (error) {
    console.error(`Error parsing date: ${dateStr} with time ${timeStr}`, error);
    return null;
  }
};

// Helper function to safely format dates
export const safeFormatDate = (date: Date | null, formatStr: string, fallback: string = 'Invalid date'): string => {
  if (!date || !isValid(date)) return fallback;
  try {
    return format(date, formatStr);
  } catch (error) {
    console.error(`Error formatting date: ${date}`, error);
    return fallback;
  }
};

export function checkDuplicate(
  newBooking: Booking,
  existingBookings: Booking[],
  excludeId?: number
): BookingConflict | null {
  // First check for direct time overlaps
  const overlappingBooking = findOverlappingBooking(newBooking, existingBookings, excludeId);
  if (overlappingBooking) {
    return {
      type: 'overlap',
      severity: 'error',
      message: `Time slot conflicts with existing booking for ${overlappingBooking.clientName}`,
      booking: overlappingBooking,
      suggestedAlternatives: generateAlternatives(newBooking, existingBookings, overlappingBooking)
    };
  }

  // Check for adjacent bookings by same client
  const adjacentBooking = findAdjacentBooking(newBooking, existingBookings, excludeId);
  if (adjacentBooking) {
    return {
      type: 'adjacent',
      severity: 'warning',
      message: `${adjacentBooking.clientName} has another booking immediately before/after this time`,
      booking: adjacentBooking,
      suggestedAlternatives: generateAlternatives(newBooking, existingBookings, adjacentBooking)
    };
  }

  // Check for same client booking on same day
  const sameClientBooking = findSameClientBooking(newBooking, existingBookings, excludeId);
  if (sameClientBooking) {
    return {
      type: 'same_client',
      severity: 'warning',
      message: `${sameClientBooking.clientName} already has a booking on this day`,
      booking: sameClientBooking,
      suggestedAlternatives: generateAlternatives(newBooking, existingBookings, sameClientBooking)
    };
  }

  return null;
}

function findOverlappingBooking(
  newBooking: Booking,
  existingBookings: Booking[],
  excludeId?: number
): Booking | null {
  return existingBookings.find(existing => {
    // Skip if excluded or different room
    if (excludeId && existing.id === excludeId) return false;
    if (existing.roomId !== newBooking.roomId) return false;
    
    try {
      // Get clean date strings 
      const existingDateClean = existing.date.includes('T') ? existing.date.split('T')[0] : existing.date;
      const newDateClean = newBooking.date.includes('T') ? newBooking.date.split('T')[0] : newBooking.date;
      
      // Skip if not same day
      if (existingDateClean !== newDateClean) return false;
      
      // Parse dates safely
      const existingStart = safeParseISO(existing.date, existing.timeSlot);
      const newStart = safeParseISO(newBooking.date, newBooking.timeSlot);
      
      if (!existingStart || !newStart) return false;

      const existingEnd = addHours(existingStart, existing.duration);
      const newEnd = addHours(newStart, newBooking.duration);

      return (
        (isAfter(newStart, existingStart) && isBefore(newStart, existingEnd)) ||
        (isAfter(existingStart, newStart) && isBefore(existingStart, newEnd)) ||
        (isBefore(newStart, existingStart) && isAfter(newEnd, existingEnd)) ||
        (isBefore(existingStart, newStart) && isAfter(existingEnd, newEnd))
      );
    } catch (error) {
      console.error('Error in findOverlappingBooking:', error);
      return false;
    }
  }) || null;
}

function findAdjacentBooking(
  newBooking: Booking,
  existingBookings: Booking[],
  excludeId?: number
): Booking | null {
  return existingBookings.find(existing => {
    // Skip if excluded or different room/client
    if (excludeId && existing.id === excludeId) return false;
    if (existing.roomId !== newBooking.roomId) return false;
    if (existing.clientName !== newBooking.clientName) return false;
    
    try {
      // Get clean date strings 
      const existingDateClean = existing.date.includes('T') ? existing.date.split('T')[0] : existing.date;
      const newDateClean = newBooking.date.includes('T') ? newBooking.date.split('T')[0] : newBooking.date;
      
      // Skip if not same day
      if (existingDateClean !== newDateClean) return false;
      
      // Parse dates safely
      const existingStart = safeParseISO(existing.date, existing.timeSlot);
      const newStart = safeParseISO(newBooking.date, newBooking.timeSlot);
      
      if (!existingStart || !newStart) return false;

      const existingEnd = addHours(existingStart, existing.duration);
      const newEnd = addHours(newStart, newBooking.duration);

      return (
        safeFormatDate(existingEnd, 'HH:mm') === safeFormatDate(newStart, 'HH:mm') ||
        safeFormatDate(newEnd, 'HH:mm') === safeFormatDate(existingStart, 'HH:mm')
      );
    } catch (error) {
      console.error('Error in findAdjacentBooking:', error);
      return false;
    }
  }) || null;
}

function findSameClientBooking(
  newBooking: Booking,
  existingBookings: Booking[],
  excludeId?: number
): Booking | null {
  return existingBookings.find(existing => {
    if (excludeId && existing.id === excludeId) return false;
    
    try {
      // Get clean date strings 
      const existingDateClean = existing.date.includes('T') ? existing.date.split('T')[0] : existing.date;
      const newDateClean = newBooking.date.includes('T') ? newBooking.date.split('T')[0] : newBooking.date;
      
      // Skip if not same day
      if (existingDateClean !== newDateClean) return false;
      
      return existing.clientName === newBooking.clientName;
    } catch (error) {
      console.error('Error in findSameClientBooking:', error);
      return false;
    }
  }) || null;
}

function generateAlternatives(
  newBooking: Booking,
  existingBookings: Booking[],
  conflictingBooking: Booking
): { date: string; time: string; room?: number }[] {
  const alternatives: { date: string; time: string; room?: number }[] = [];
  
  try {
    // Get a valid date from the booking or create fallback
    let day: Date;
    if (newBooking.date.includes('T')) {
      day = parseISO(newBooking.date);
    } else {
      const parsedDate = safeParseISO(newBooking.date, '00:00');
      if (!parsedDate) {
        day = new Date(); // Fallback to today
      } else {
        day = parsedDate;
      }
    }

    // Try same day, different times
    const timeSlots = [
      '09:00', '10:00', '11:00', '12:00', '13:00', '14:00',
      '15:00', '16:00', '17:00', '18:00', '19:00', '20:00',
      '21:00', '22:00', '23:00', '00:00', '01:00', '02:00'
    ];

    // Get just the date part as a string
    const dateStr = format(day, 'yyyy-MM-dd');

    // Check each time slot
    for (const time of timeSlots) {
      const isAvailable = !existingBookings.some(booking => {
        if (booking.roomId !== newBooking.roomId) return false;
        
        try {
          const bookingDateClean = booking.date.includes('T') 
            ? booking.date.split('T')[0] 
            : booking.date;
            
          if (bookingDateClean !== dateStr) return false;
          
          const bookingStart = safeParseISO(booking.date, booking.timeSlot);
          if (!bookingStart) return false;
          
          const bookingEnd = addHours(bookingStart, booking.duration);
          const testStart = safeParseISO(dateStr, time);
          if (!testStart) return false;
          
          const testEnd = addHours(testStart, newBooking.duration);

          return (
            (isAfter(testStart, bookingStart) && isBefore(testStart, bookingEnd)) ||
            (isAfter(bookingStart, testStart) && isBefore(bookingStart, testEnd))
          );
        } catch (error) {
          console.error('Error checking time slot availability:', error);
          return false;
        }
      });

      if (isAvailable) {
        alternatives.push({
          date: dateStr,
          time
        });
      }

      // Limit to 3 alternatives per day
      if (alternatives.length >= 3) break;
    }

    // If we couldn't find alternatives on the same day, try next day
    if (alternatives.length === 0) {
      const nextDay = addDays(day, 1);
      alternatives.push({
        date: format(nextDay, 'yyyy-MM-dd'),
        time: '09:00'
      });
    }
  } catch (error) {
    console.error('Error generating alternatives:', error);
    // Add a default alternative if everything fails
    alternatives.push({
      date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
      time: '09:00'
    });
  }

  return alternatives;
}

export function validateBookingTime(booking: Booking): string | null {
  try {
    const startHour = parseInt(booking.timeSlot.split(':')[0]);
    
    // Check for NaN
    if (isNaN(startHour)) {
      return "Invalid time format";
    }
    
    // Validate full day bookings
    if (booking.duration === 14) {
      if (startHour !== 9) {
        return "Full day bookings must start at 09:00";
      }
      return null;
    }

    // Validate regular bookings
    if (startHour < 9 && startHour > 2) {
      return "Bookings can only start between 09:00-23:59 and 00:00-02:00";
    }

    const endHour = (startHour + booking.duration) % 24;
    if (endHour > 2 && endHour < 9) {
      return "Bookings cannot end between 02:00 and 09:00";
    }

    return null;
  } catch (error) {
    console.error('Error validating booking time:', error);
    return "Invalid time format";
  }
}